'use client';

import React from 'react';

export interface ButtonProps {
  /** Visual style. One filled `primary` per screen. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  /** Control height: sm 36 · md 44 · lg 52 (primary). */
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
  /** Icon node placed at the inline-start (flips under RTL). */
  leadingIcon?: React.ReactNode;
  /** Icon node placed at the inline-end. */
  trailingIcon?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  children?: React.ReactNode;
  style?: React.CSSProperties;
}

/**
 * Ink & Whisper primary button. One filled-accent primary per screen.
 * RTL-safe (logical padding, icons flip with content).
 */
export function Button({
  variant = 'primary',
  size = 'lg',
  fullWidth = false,
  disabled = false,
  leadingIcon = null,
  trailingIcon = null,
  onClick,
  type = 'button',
  children,
  style = {},
  ...rest
}: ButtonProps & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonProps>) {
  const heights = { sm: 36, md: 44, lg: 52 };
  const fontSizes = { sm: 'var(--fs-caption)', md: 'var(--fs-body)', lg: 'var(--fs-body-l)' };
  const padX = { sm: 14, md: 18, lg: 22 };

  const palette = {
    primary: {
      background: 'var(--brand)',
      color: 'var(--on-brand)',
      border: '1px solid transparent',
    },
    secondary: {
      background: 'var(--bg-surface)',
      color: 'var(--text-primary)',
      border: '1px solid var(--border-strong)',
    },
    ghost: {
      background: 'transparent',
      color: 'var(--brand)',
      border: '1px solid transparent',
    },
    danger: {
      background: 'transparent',
      color: 'var(--error)',
      border: '1px solid var(--error)',
    },
  }[variant];

  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--sp-3)',
    height: heights[size],
    paddingInline: padX[size],
    width: fullWidth ? '100%' : 'auto',
    fontFamily: 'var(--font-sans)',
    fontSize: fontSizes[size],
    fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
    lineHeight: 1,
    // Pill, not `--r-md`. The house recipe is `borderRadius: 999` for both the
    // ink primary and the hairline ghost (slikerr-design, components.md #1/#2),
    // and the native app has always drawn them that way — web was the odd one
    // out. Changed 2026-08-18 so every web surface matches the app.
    borderRadius: 'var(--r-full)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
    transition:
      'background var(--dur-micro) var(--ease-standard), transform var(--dur-micro) var(--ease-standard)',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    ...palette,
    ...style,
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={base}
      onMouseDown={(e) => {
        if (!disabled) e.currentTarget.style.transform = 'scale(0.98)';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      {...rest}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}
